/**
 * 统一打印服务入口
 *
 * 自动检测可用的打印方案（按优先级）：
 * 1. QZ Tray      - 开源免费，跨平台，静默打印 PDF，质量好
 * 2. print-server  - Mac 本地 lp 命令
 *
 * 两者都不可用时返回 ready: false
 */

import {
    checkPrintService as checkQz,
    qzPrintPdf,
    listPrinters as qzListPrinters,
    PrintOptions as QzPrintOptions,
} from './qzPrintService';

import {
    checkPrintService as checkLocal,
    localPrintPdf,
    PrintOptions as LocalPrintOptions,
} from './localPrintService';

export interface PrintOptions {
    printer?: string;
    copies?: number;
    pageSize?: string;
    resolution?: string;
    fitToPage?: boolean;
}

type PrintBackend = 'qz' | 'local' | null;

let activeBackend: PrintBackend = null;

/**
 * 检测打印服务是否可用，自动选择后端
 * 并行探测所有后端，按优先级选择第一个可用的
 */
export const checkPrintService = async (): Promise<{
    ready: boolean;
    needsCertSetup: boolean;
    printers: string[];
    defaultPrinter: string;
    backend: PrintBackend;
}> => {
    const [qzStatus, localStatus] = await Promise.all([
        checkQz().catch(() => ({ ready: false as const, needsCertSetup: false, printers: [] as string[], defaultPrinter: '' })),
        checkLocal().catch(() => ({ ready: false as const, printers: [] as string[], defaultPrinter: '' })),
    ]);

    // 按优先级选择: QZ Tray > Local
    if (qzStatus.ready) {
        activeBackend = 'qz';
        return { ...qzStatus, backend: 'qz' };
    }
    if (localStatus.ready) {
        activeBackend = 'local';
        return { ...localStatus, needsCertSetup: false, backend: 'local' };
    }

    activeBackend = null;
    return { ready: false, needsCertSetup: qzStatus.needsCertSetup, printers: [], defaultPrinter: '', backend: null };
};

/**
 * 使用当前活跃的后端打印 PDF
 */
/**
 * 获取完整打印机列表（延迟加载，打开设置时调用）
 */
export const fetchPrinterList = async (): Promise<string[]> => {
    if (activeBackend === 'qz') {
        return qzListPrinters();
    }
    return [];
};

export const printPdf = async (
    pdfBase64: string,
    options?: PrintOptions,
): Promise<void> => {
    if (activeBackend === 'qz') {
        await qzPrintPdf(pdfBase64, options as QzPrintOptions);
    } else if (activeBackend === 'local') {
        await localPrintPdf(pdfBase64, options as LocalPrintOptions);
    } else {
        throw new Error('No print service available');
    }
};
